import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Trash, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import { StatusBadge } from "../components/Semaforo";
import { MEETING_TIPOS } from "../lib/constants";
import { formatDate, formatTime } from "../lib/format";
import { useConfirm } from "../components/ConfirmDialog";

export default function Meetings() {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [authorities, setAuthorities] = useState([]);
  const [params] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const nav = useNavigate();

  function empty() {
    return {
      asunto: "", fecha: "", hora: "", lugar: "", tipo: "Coordinación",
      related_entity_type: params.get("related_entity_type") || "",
      related_entity_id: params.get("related_entity_id") || "",
      asistentes_internos: [], asistentes_externos: [],
      responsable_seguimiento: "", estado_seguimiento: "abierto",
    };
  }

  const load = async () => {
    const relType = params.get("related_entity_type");
    const relId = params.get("related_entity_id");
    const url = relType && relId ? `/meetings?related_entity_type=${relType}&related_entity_id=${relId}` : "/meetings";
    const { data } = await api.get(url);
    setItems(data);
  };
  useEffect(() => {
    load();
    api.get("/communities").then((r) => setCommunities(r.data));
    api.get("/authorities").then((r) => setAuthorities(r.data));
    if (params.get("new") === "1") setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const submit = async () => {
    try {
      const p = { ...form };
      if (!p.related_entity_type) { p.related_entity_type = null; p.related_entity_id = null; }
      if (!p.related_entity_id) p.related_entity_id = null;
      if (!p.hora) p.hora = null;
      await api.post("/meetings", p);
      toast.success("Reunión creada");
      setOpen(false); setForm(empty()); load();
    } catch (e) { toast.error("Error"); }
  };

  const remove = async (id) => { if (!(await confirm({ title: "Eliminar reunión", description: "¿Deseas eliminar esta reunión? Esta acción no se puede deshacer.", danger: true }))) return; await api.delete(`/meetings/${id}`); load(); };

  const relatedEntities = form.related_entity_type === "community" ? communities : form.related_entity_type === "authority" ? authorities : [];

  return (
    <div data-testid="meetings-page">
      <PageHeader title="Reuniones, minutas y acuerdos" description="Bitácora de reuniones con comunidades, autoridades y organizaciones."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-slate-900 text-white" data-testid="new-meeting-btn"><Plus weight="bold" /> Nueva reunión</Button></DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader><DialogTitle>Nueva reunión</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Label>Asunto</Label><Input value={form.asunto} onChange={(e) => setForm({ ...form, asunto: e.target.value })} data-testid="meeting-asunto-input" /></div>
                <div><Label>Fecha</Label><Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} /></div>
                <div><Label>Hora</Label><Input type="time" value={form.hora} onChange={(e) => setForm({ ...form, hora: e.target.value })} /></div>
                <div><Label>Lugar</Label><Input value={form.lugar} onChange={(e) => setForm({ ...form, lugar: e.target.value })} /></div>
                <div>
                  <Label>Tipo</Label>
                  <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                    <SelectTrigger><SelectValue placeholder="Selecciona tipo" /></SelectTrigger>
                    <SelectContent>
                      {MEETING_TIPOS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Vincular a</Label>
                  <Select value={form.related_entity_type || "__none__"} onValueChange={(v) => setForm({ ...form, related_entity_type: v === "__none__" ? "" : v, related_entity_id: "" })}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">—</SelectItem>
                      <SelectItem value="community">Comunidad</SelectItem>
                      <SelectItem value="authority">Autoridad</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Entidad</Label>
                  <Select value={form.related_entity_id || "__none__"} onValueChange={(v) => setForm({ ...form, related_entity_id: v === "__none__" ? "" : v })} disabled={!form.related_entity_type}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">—</SelectItem>
                      {relatedEntities.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Responsable seguimiento</Label><Input value={form.responsable_seguimiento} onChange={(e) => setForm({ ...form, responsable_seguimiento: e.target.value })} /></div>
                <div className="col-span-2">
                  <Label>Estado seguimiento</Label>
                  <Select value={form.estado_seguimiento} onValueChange={(v) => setForm({ ...form, estado_seguimiento: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="abierto">Abierto</SelectItem>
                      <SelectItem value="en_seguimiento">En seguimiento</SelectItem>
                      <SelectItem value="cerrado">Cerrado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button onClick={submit} className="bg-slate-900 text-white" data-testid="save-meeting-btn">Guardar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="px-6 py-6">
        <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
          <Table>
            <TableHeader><TableRow><TableHead>Asunto</TableHead><TableHead>Fecha</TableHead><TableHead>Lugar</TableHead><TableHead>Tipo</TableHead><TableHead>Estado</TableHead><TableHead className="text-right">—</TableHead></TableRow></TableHeader>
            <TableBody>
              {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-500">Sin reuniones.</TableCell></TableRow>}
              {items.map(m => (
                <TableRow key={m.id} className="cursor-pointer hover:bg-slate-50" onClick={() => nav(`/reuniones/${m.id}`)} data-testid={`meeting-row-${m.id}`}>
                  <TableCell className="font-medium">
                    <Link to={`/reuniones/${m.id}`} className="hover:underline">{m.asunto}</Link>
                    <div className="text-xs text-slate-500">{m.acuerdos ? `${m.acuerdos.slice(0,80)}…` : ""}</div>
                  </TableCell>
                  <TableCell>{formatDate(m.fecha)} {m.hora ? <span className="text-xs text-slate-500">{formatTime(m.hora)}</span> : ""}</TableCell>
                  <TableCell>{m.lugar || "—"}</TableCell>
                  <TableCell>{m.tipo || "—"}</TableCell>
                  <TableCell><StatusBadge tone="info">{m.estado_seguimiento}</StatusBadge></TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <Link to={`/reuniones/${m.id}`}><Button size="sm" variant="ghost"><ArrowSquareOut size={14} /></Button></Link>
                    <Button size="sm" variant="ghost" onClick={() => remove(m.id)}><Trash size={14} className="text-red-600" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
